import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { useNavigate, Link } from 'react-router-dom';
import { useDateRange } from '../context/DateContext';
import { useUser } from '../context/UserContext';
import dayjs from 'dayjs';
import { HASH } from '../utils/helpers';
import { login } from '../services/api';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { updateUser } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { setStartDate, setEndDate } = useDateRange();

  // Verificar si los campos están rellenos
  const isFormFilled = email && password;

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const hashedPassword = HASH(password);
      const userData = await login(email, hashedPassword);
      
      // Transformar los productos a la estructura de asistentes para mantener compatibilidad
      const assistants = userData.products ? userData.products.map(product => ({
        assistant: product.code,
        name: product.name
      })) : [];

      // Verificar si hay asistentes disponibles
      if (assistants.length === 0) {
        setError('No hay asistentes disponibles para este usuario');
        return;
      }

      // Crear el objeto de usuario final con los asistentes transformados
      const transformedUserData = {
        ...userData,
        assistants
      };

      // Actualizar el contexto del usuario
      updateUser(transformedUserData);
      
      // Si el login es exitoso, configurar fechas por defecto y redirigir
      const today = dayjs();
      setStartDate(today.subtract(7, 'day'));
      setEndDate(today);
      
      navigate('/');
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        backgroundImage: theme.palette.mode === 'dark'
          ? `
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)
          `
          : `
            linear-gradient(rgba(15,23,42,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15,23,42,0.05) 1px, transparent 1px)
          `,
        backgroundSize: '48px 48px, 48px 48px',
        px: 2,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 460,
            backdropFilter: 'blur(10px)',
            borderRadius: '28px',
            bgcolor: 'background.paper',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0,0,0,0.32)'
              : '0 20px 60px rgba(15,23,42,0.08)',
          }}
        >
          <Box
            sx={{
              mb: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 8px 16px rgba(249, 115, 22, 0.25)'
            }}>
              <LockOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'secondary.main',
                mb: 1,
                letterSpacing: '-0.02em',
                fontSize: { xs: '2.35rem', sm: '2.6rem' },
              }}
            >
              AI<span style={{ color: '#f97316' }}>4</span>Assistant
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 1,
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            >
              AI Assistant Engine
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }} autoComplete="on" method="POST">
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 4, borderRadius: '12px', fontWeight: 600 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="username"
              autoComplete="username email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '14px' }
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.light,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                input: { color: theme.palette.text.primary },
                label: { color: theme.palette.text.secondary }
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenOutlinedIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small" onClick={handleClickShowPassword} sx={{ color: theme.palette.text.secondary }}>
                      {showPassword ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: '14px' }
              }}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.light,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                input: { color: theme.palette.text.primary },
                label: { color: theme.palette.text.secondary }
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!isFormFilled}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: '14px',
                textTransform: 'none',
                height: 56,
                boxShadow: isFormFilled ? '0 10px 24px rgba(249, 115, 22, 0.24)' : 'none',
                '&:hover': {
                  boxShadow: isFormFilled ? '0 12px 28px rgba(249, 115, 22, 0.28)' : 'none',
                },
              }}
            >
              Acceder al Sistema
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                component={Link}
                to="/recuperar-password"
                variant="text"
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                ¿Olvidaste tu contraseña?
              </Button>
            </Box>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em' }}>
                © 2026 AI4Logic • AI4Assistant
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
